<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import FieldTypeIcon from './FieldTypeIcon.svelte';
  import Icon from './Icon.svelte';
  import type { ApiField, ApiRecord } from '../lib/data-tables-api';
  import { fieldType } from '../lib/data-tables-catalog';
  import FieldEditor from '../lib/fields/FieldEditor.svelte';

  let {
    open,
    fields,
    record,
    onSave,
    onCancel,
    onDelete,
    busy = false,
  }: {
    open: boolean;
    fields: ApiField[];
    /** Якщо null — режим створення. */
    record: ApiRecord | null;
    onSave: (values: Record<string, unknown>) => void;
    onCancel: () => void;
    onDelete?: () => void;
    busy?: boolean;
  } = $props();

  // Локальні значення форми. Reseta'мо при кожному (від)критті.
  let values = $state<Record<string, unknown>>({});
  let prevOpen = false;
  $effect(() => {
    if (open && !prevOpen) {
      values = record ? { ...record.values } : {};
    }
    prevOpen = open;
  });

  function setValue(key: string, v: unknown) {
    values = { ...values, [key]: v };
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    // Required-check.
    for (const f of fields) {
      if (f.required && !f.is_computed) {
        const v = values[f.fieldKey];
        if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
          showAlert(`Обов'язкове поле: ${f.label}`);
          return;
        }
      }
    }
    onSave(values);
  }

  // Computed-поля виключаємо з форми створення/редагування.
  const editableFields = $derived(fields.filter((f) => !f.is_computed));
</script>

{#if open}
  <div class="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onclick={onCancel}>
    <form
      onsubmit={submit}
      onclick={(e) => e.stopPropagation()}
      class="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl"
    >
      <div class="flex items-center justify-between border-b border-[var(--color-border-3)] px-5 py-3">
        <h3 class="text-base font-bold">
          {record ? 'Редагувати запис' : 'Новий запис'}
        </h3>
        <button type="button" onclick={onCancel} class="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><Icon name="close" size={16} /></button>
      </div>

      <div class="px-5 py-4 space-y-3">
        {#each editableFields as f (f.id)}
          {@const ft = fieldType(f.type)}
          <label class="block">
            <div class="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">
              <span><FieldTypeIcon type={ft.key} size={14} /></span>
              <span>{f.label}</span>
              {#if f.required}<span class="text-red-500 normal-case">*</span>{/if}
            </div>
            <FieldEditor field={f} value={values[f.fieldKey]} onChange={(v) => setValue(f.fieldKey, v)} />
          </label>
        {/each}

        {#if editableFields.length === 0}
          <div class="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            У цієї таблиці немає редагованих полів. Додайте поля у
            <a class="underline" href="#/settings/data-structure">Налаштуваннях → Структура даних</a>.
          </div>
        {/if}
      </div>

      <div class="flex items-center justify-between gap-2 border-t border-[var(--color-border-3)] px-5 py-3">
        <div>
          {#if record && onDelete}
            <button
              type="button"
              onclick={onDelete}
              disabled={busy}
              class="rounded-md border border-red-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Видалити
            </button>
          {/if}
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            onclick={onCancel}
            class="rounded-md border border-[var(--color-border-3)] bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
          >
            Скасувати
          </button>
          <button
            type="submit"
            disabled={busy || editableFields.length === 0}
            class="rounded-md bg-[var(--color-pu)] px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
          >
            {busy ? 'Збереження…' : 'Зберегти'}
          </button>
        </div>
      </div>
    </form>
  </div>
{/if}
